/* ------------------------------------------------------------------
  File: StatCard.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable statistic card component for the Accend Holdings website.
//   - Supports value, label, description, optional eyebrow, optional icon,
//     trend/status text, and card styling options.
//   - Uses BaseCard as the foundational shell.
//   - Uses the acc-stat-card namespace to avoid conflicts with Bootstrap.
//   - Suitable for company metrics, impact numbers, project outcomes,
//     investor highlights, and operational statistics.

/**
 * StatCard
 *
 * A reusable statistic card component for displaying metrics and highlights.
 *
 * @param {string | number} value
 * Main statistic value.
 *
 * @param {string} label
 * Short label describing the statistic.
 *
 * @param {string} description
 * Optional supporting description.
 *
 * @param {string} eyebrow
 * Optional small label displayed above the value.
 *
 * @param {React.ReactNode} icon
 * Optional icon displayed inside the card.
 *
 * @param {string} trend
 * Optional trend or status text.
 *
 * @param {"default" | "muted" | "soft" | "inverse"} [variant="default"]
 * Controls the BaseCard surface style.
 *
 * @param {"none" | "sm" | "md" | "lg"} [padding="md"]
 * Controls BaseCard internal spacing.
 *
 * @param {boolean} [hoverable=false]
 * Enables BaseCard hover behavior.
 *
 * @param {boolean} [bordered=true]
 * Controls whether the BaseCard has a border.
 *
 * @param {boolean} [fullHeight=false]
 * Makes the card stretch to the full height of its parent.
 *
 * @param {"left" | "center"} [align="left"]
 * Controls stat card text alignment.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import BaseCard from "./BaseCard";
import "./StatCard.css";

const StatCard = ({
  value,
  label,
  description,
  eyebrow,
  icon,
  trend,
  variant = "default",
  padding = "md",
  hoverable = false,
  bordered = true,
  fullHeight = false,
  align = "left",
  className = "",
  ...props
}) => {
  const statCardClasses = [
    "acc-stat-card",
    `acc-stat-card--${align}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <BaseCard
      as="article"
      variant={variant}
      padding={padding}
      hoverable={hoverable}
      bordered={bordered}
      fullHeight={fullHeight}
      className={statCardClasses}
      {...props}
    >
      <div className="acc-stat-card__inner l-stack l-stack--sm">
        {(icon || eyebrow) && (
          <div className="acc-stat-card__top l-stack l-stack--xs">
            {icon && (
              <span className="acc-stat-card__icon" aria-hidden="true">
                {icon}
              </span>
            )}

            {eyebrow && (
              <span className="acc-stat-card__eyebrow">{eyebrow}</span>
            )}
          </div>
        )}

        <div className="acc-stat-card__content l-stack l-stack--xs">
          {value !== undefined && value !== null && (
            <p className="acc-stat-card__value">{value}</p>
          )}

          {label && <h3 className="acc-stat-card__label">{label}</h3>}

          {description && (
            <p className="acc-stat-card__description">{description}</p>
          )}
        </div>

        {trend && <p className="acc-stat-card__trend">{trend}</p>}
      </div>
    </BaseCard>
  );
};

export default StatCard;